<template>
  <div class="content d-flex flex-column flex-column-fluid" id="kt_content">
    <div class="toolbar" id="kt_toolbar">
      <div id="kt_toolbar_container" class="container-fluid d-flex flex-stack">
        <div class="page-title d-flex align-items-center flex-wrap me-3 mb-5 mb-lg-0">
          <h1 class="d-flex align-items-center text-dark fw-bolder fs-3 my-1">
            {{ $t('landlord.societies.detail_title') }}
          </h1>
          <span class="h-20px border-gray-300 border-start mx-4"></span>
          <span class="text-muted fs-6">{{ society?.name || societyId }}</span>
        </div>
        <div class="d-flex align-items-center gap-2">
          <a v-if="society" href="#" @click.prevent="openEditModal" class="btn btn-sm btn-light-primary">
            <i class="fas fa-pen me-1"></i> {{ $t('landlord.societies.edit') }}
          </a>
          <router-link :to="`/landlord/shops/create?society_id=${societyId}`" class="btn btn-sm btn-primary">
            <i class="fas fa-plus me-1"></i> {{ $t('landlord.societies.add_shop') }}
          </router-link>
          <router-link to="/landlord/societies" class="btn btn-sm btn-light">
            <i class="fas fa-arrow-left me-1"></i> {{ $t('landlord.societies.back') }}
          </router-link>
        </div>
      </div>
    </div>
    <div class="post d-flex flex-column-fluid" id="kt_post">
      <div id="kt_content_container" class="container-fluid">

        <div v-if="loading" class="text-center py-20">
          <span class="spinner-border text-primary"></span>
        </div>

        <template v-else-if="society">
          <!-- Header card -->
          <div class="card card-flush mb-5">
            <div class="card-body d-flex align-items-center justify-content-between py-5 flex-wrap gap-3">
              <div class="d-flex align-items-center gap-4">
                <div class="rounded bg-light-primary d-flex align-items-center justify-content-center" style="width:56px;height:56px;">
                  <i class="fas fa-building fs-2 text-primary"></i>
                </div>
                <div>
                  <h2 class="fw-bolder mb-1">{{ society.name || societyId }}</h2>
                  <div class="text-muted fs-7">{{ society.email || '-' }}</div>
                </div>
              </div>
              <div class="d-flex gap-8">
                <div class="text-center">
                  <div class="fs-2 fw-bolder">{{ society.shops_count }}</div>
                  <div class="text-muted fs-7">{{ $t('landlord.societies.col_shops_count') }}</div>
                </div>
                <div class="text-center">
                  <div class="fs-6 fw-bolder">{{ society.plan || '-' }}</div>
                  <div class="text-muted fs-7">{{ $t('landlord.societies.col_plan') }}</div>
                </div>
                <div class="text-center">
                  <div class="fs-2 fw-bolder">{{ society.effective_max_shops > 0 ? society.effective_max_shops : '∞' }}</div>
                  <div class="text-muted fs-7">{{ $t('landlord.societies.col_max_shops') }}</div>
                </div>
                <!-- Utilisateurs actifs / limite de l'abonnement, toutes boutiques confondues -->
                <div class="text-center">
                  <div class="fs-2 fw-bolder">{{ society.active_users_count ?? 0 }} / {{ society.effective_max_users > 0 ? society.effective_max_users : '∞' }}</div>
                  <div class="text-muted fs-7">{{ $t('landlord.societies.col_users') }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Owner + contact -->
          <div class="card card-flush mb-5">
            <div class="card-header border-0 pt-6">
              <h3 class="card-title fw-bolder">{{ $t('landlord.societies.owner_section') }}</h3>
            </div>
            <div class="card-body py-4">
              <div v-if="society.owner" class="row g-5">
                <div class="col-md-4">
                  <div class="text-muted fs-7">{{ $t('landlord.societies.col_owner') }}</div>
                  <div class="fw-bold">{{ society.owner.name }}</div>
                </div>
                <div class="col-md-4">
                  <div class="text-muted fs-7">Email</div>
                  <div class="fw-bold">{{ society.owner.email }}</div>
                </div>
                <div class="col-md-4">
                  <div class="text-muted fs-7">{{ $t('landlord.shops.fields.phone') }}</div>
                  <div class="fw-bold">{{ [society.phone_code, society.phone].filter(Boolean).join(' ') || '-' }}</div>
                </div>
                <div class="col-md-4">
                  <div class="text-muted fs-7">{{ $t('landlord.shops.fields.ifu_number') }}</div>
                  <div class="fw-bold">{{ society.ifu_number || '-' }}</div>
                </div>
                <div class="col-md-4">
                  <div class="text-muted fs-7">{{ $t('landlord.shops.fields.rccm_number') }}</div>
                  <div class="fw-bold">{{ society.rccm_number || '-' }}</div>
                </div>
              </div>
              <div v-else class="text-gray-400">{{ $t('landlord.societies.no_owner') }}</div>
            </div>
          </div>

          <!-- Shops list -->
          <div class="card card-flush">
            <div class="card-header border-0 pt-6">
              <h3 class="card-title fw-bolder">{{ $t('landlord.societies.shops_section') }}</h3>
            </div>
            <div class="card-body py-4">
              <div v-if="!society.shops || !society.shops.length" class="text-center py-10 text-gray-500">
                {{ $t('landlord.societies.no_shops') }}
              </div>
              <div v-else class="table-responsive">
                <table class="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                  <thead>
                    <tr class="fw-bolder text-muted">
                      <th>{{ $t('landlord.shops.col_name') }}</th>
                      <th>{{ $t('landlord.shops.col_phone') }}</th>
                      <th>{{ $t('landlord.shops.col_domain') }}</th>
                      <th>{{ $t('landlord.shops.col_status') }}</th>
                      <th class="text-end">{{ $t('landlord.shops.col_actions') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="shop in society.shops" :key="shop.id">
                      <td class="fw-bold">{{ shop.name || shop.id }}</td>
                      <td>{{ shop.phone ? [shop.phone_code, shop.phone].filter(Boolean).join(' ') : '-' }}</td>
                      <td>
                        <span v-if="shop.domain" class="badge badge-light-primary">{{ shop.domain }}</span>
                        <span v-else class="text-gray-400">-</span>
                      </td>
                      <td>
                        <span class="badge" :class="shop.online ? 'badge-light-success' : 'badge-light-danger'">
                          {{ shop.online ? $t('landlord.shops.active') : $t('landlord.shops.inactive') }}
                        </span>
                      </td>
                      <td class="text-end">
                        <router-link :to="`/landlord/shops/${shop.id}`" class="btn btn-icon btn-sm btn-light-primary" :title="$t('landlord.shops.view')">
                          <i class="fas fa-eye"></i>
                        </router-link>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Numéro WhatsApp de la société (commun à toutes ses boutiques) -->
          <WhatsappSettingsForm
              class="mt-5"
              :http="landlordHttp"
              :url="`landlord/societies/${societyId}/whatsapp`"
              method="put"
              title="Numéro WhatsApp de la société"
              description="Numéro commun à toutes les boutiques de cette société, depuis lequel partent les messages à ses clients (codes OTP e-commerce, factures). À défaut, le numéro de la plateforme est utilisé."
          />
        </template>
      </div>
    </div>

    <!-- Edit modal -->
    <div class="modal fade" id="editSocietyModal" data-backdrop="static" tabindex="-1" role="dialog" aria-hidden="true">
      <div class="modal-dialog" role="document">
        <form @submit.prevent="submitEdit" class="form">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">{{ $t('landlord.societies.edit') }}</h5>
              <div class="btn btn-icon btn-sm btn-active-light-primary ms-2" data-bs-dismiss="modal" aria-label="Close">
                <i class="fas fa-times"></i>
              </div>
            </div>
            <div class="modal-body">
              <div class="d-flex flex-column mb-5 fv-row">
                <label class="form-label fs-6 fw-bold mb-2 required">{{ $t('landlord.shops.fields.society_name') }}</label>
                <input v-model="editForm.name" type="text" class="form-control form-control-solid" required />
                <span v-if="editErrors.name" class="text-danger fs-8">{{ editErrors.name[0] }}</span>
              </div>
              <div class="d-flex flex-column mb-5 fv-row">
                <label class="form-label fs-6 fw-bold mb-2">{{ $t('landlord.shops.fields.society_email') }}</label>
                <input v-model="editForm.email" type="email" class="form-control form-control-solid" />
                <span v-if="editErrors.email" class="text-danger fs-8">{{ editErrors.email[0] }}</span>
              </div>
              <div class="d-flex flex-column mb-5 fv-row">
                <InputTel
                  name="edit-society-phone"
                  :label="$t('landlord.shops.fields.phone')"
                  containerClass=""
                  labelClass="form-label fs-6 fw-bold mb-2"
                  v-model:phone="editForm.phone"
                  v-model:phone-code="editForm.phone_code"
                />
                <span v-if="editErrors.phone" class="text-danger fs-8">{{ editErrors.phone[0] }}</span>
              </div>
              <div class="d-flex flex-column mb-5 fv-row">
                <label class="form-label fs-6 fw-bold mb-2">{{ $t('landlord.shops.fields.ifu_number') }}</label>
                <input v-model="editForm.ifu_number" type="text" class="form-control form-control-solid" />
                <span v-if="editErrors.ifu_number" class="text-danger fs-8">{{ editErrors.ifu_number[0] }}</span>
              </div>
              <div class="d-flex flex-column mb-5 fv-row">
                <label class="form-label fs-6 fw-bold mb-2">{{ $t('landlord.shops.fields.rccm_number') }}</label>
                <input v-model="editForm.rccm_number" type="text" class="form-control form-control-solid" />
                <span v-if="editErrors.rccm_number" class="text-danger fs-8">{{ editErrors.rccm_number[0] }}</span>
              </div>
              <div class="d-flex flex-column mb-5 fv-row">
                <label class="form-label fs-6 fw-bold mb-2">{{ $t('landlord.societies.field_max_shops') }}</label>
                <input v-model="editForm.max_shops" type="number" min="0" step="1" class="form-control form-control-solid" placeholder="1" />
                <span class="form-text text-muted">{{ $t('landlord.societies.field_max_shops_hint') }}</span>
                <span v-if="editErrors.max_shops" class="text-danger fs-8">{{ editErrors.max_shops[0] }}</span>
              </div>
              <div class="d-flex flex-column mb-5 fv-row">
                <label class="form-label fs-6 fw-bold mb-2">{{ $t('landlord.societies.field_max_users') }}</label>
                <input v-model="editForm.max_users" type="number" min="0" step="1" class="form-control form-control-solid" :placeholder="$t('landlord.societies.field_max_users_placeholder')" />
                <span class="form-text text-muted">{{ $t('landlord.societies.field_max_users_hint') }}</span>
                <span v-if="editErrors.max_users" class="text-danger fs-8">{{ editErrors.max_users[0] }}</span>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-sm btn-light" data-bs-dismiss="modal">{{ $t('button.close') }}</button>
              <button :disabled="editSubmitting" type="submit" class="btn btn-sm btn-primary fw-bolder">
                {{ $t('button.save') }}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import InputTel from '@/components/InputTel.vue'
import WhatsappSettingsForm from '@/components/WhatsappSettingsForm.vue'
import landlordAxios from '@/services/landlordAxios'

export default {
    name: 'LandlordSocietyView',
    components: { InputTel, WhatsappSettingsForm },
    data() {
        return {
            landlordHttp: landlordAxios,
            loading: false,
            editSubmitting: false,
            editErrors: {},
            editForm: {
                name: '',
                email: '',
                phone: '',
                phone_code: '',
                ifu_number: '',
                rccm_number: '',
                max_shops: '',
            },
        }
    },
    computed: {
        ...mapGetters('landlord', { society: 'currentSociety' }),
        societyId() {
            return this.$route.params.id
        },
    },
    mounted() {
        this.loading = true
        this.fetchSociety(this.societyId).finally(() => { this.loading = false })
    },
    methods: {
        ...mapActions('landlord', ['fetchSociety', 'updateSociety']),

        openEditModal() {
            this.editErrors = {}
            this.editForm = {
                name: this.society.name || '',
                email: this.society.email || '',
                phone: this.society.phone || '',
                phone_code: this.society.phone_code || '',
                ifu_number: this.society.ifu_number || '',
                rccm_number: this.society.rccm_number || '',
                max_shops: this.society.max_shops === null || this.society.max_shops === undefined ? '' : this.society.max_shops,
                max_users: this.society.max_users === null || this.society.max_users === undefined ? '' : this.society.max_users,
            }
            $('#editSocietyModal').modal('toggle')
        },

        submitEdit() {
            this.editSubmitting = true
            this.editErrors = {}
            const payload = {
                ...this.editForm,
                // Champ vide = pas de dérogation, on retombe sur la limite du plan.
                max_shops: this.editForm.max_shops === '' ? null : Number(this.editForm.max_shops),
                max_users: this.editForm.max_users === '' ? null : Number(this.editForm.max_users),
            }
            this.updateSociety({ societyId: this.societyId, data: payload })
                .then(() => {
                    $('#editSocietyModal').modal('toggle')
                    window.toastr['success'](this.$t('landlord.societies.updated'))
                })
                .catch((err) => {
                    this.editErrors = err.response?.data?.errors || {}
                    window.toastr['error'](err.response?.data?.message || 'Une erreur est survenue')
                })
                .finally(() => { this.editSubmitting = false })
        },
    }
}
</script>
